const MS_PER_DAY = 86_400_000;

export interface CycleCountProduct {
  productId: bigint;
  locationId: bigint;
  metric: number;
  hasOpenCount: boolean;
  lastCountedAt: Date | null;
}

export interface CycleCount {
  locationId: bigint;
  productIds: bigint[];
}

export interface BuildCycleCountNameInput {
  ruleName: string;
  createdAt: Date;
}

// UTC so a stored name and a client-derived fallback agree for the same instant.
export function buildCycleCountName({
  ruleName,
  createdAt,
}: BuildCycleCountNameInput): string {
  const date = createdAt.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
  return `${ruleName} · ${date}`;
}

export interface BuildContext<R> {
  rule: R;
  now: Date;
  ignoreSchedule?: boolean;
}

export function daysSinceLive(liveAt: Date, now: Date): number {
  return Math.floor((now.getTime() - liveAt.getTime()) / MS_PER_DAY);
}

export function isStaleBeyond(
  lastCountedAt: Date | null,
  windowDays: number,
  now: Date,
): boolean {
  if (lastCountedAt == null) {
    return true;
  }
  return now.getTime() - lastCountedAt.getTime() > windowDays * MS_PER_DAY;
}

export function groupProductsByLocation(
  products: { locationId: bigint; productId: bigint }[],
): CycleCount[] {
  const byLocation = new Map<bigint, Set<bigint>>();
  for (const product of products) {
    const productIds = byLocation.get(product.locationId) ?? new Set();
    productIds.add(product.productId);
    byLocation.set(product.locationId, productIds);
  }
  return [...byLocation.entries()].map(([locationId, productIds]) => ({
    locationId,
    productIds: [...productIds],
  }));
}
